import { useState } from 'react';
import { useAssignReferralType as useAssignReferralTypeMutation } from '@xhype/card-app-sdk';
import { useAuthStore } from '@package/smart-wallet/store';
import * as Sentry from '@sentry/react';

/**
 * Hook to assign a referral type to the current user
 */
export const useAssignReferralType = () => {
  const id = useAuthStore((store) => store.id);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState(false);

  // Mutation to assign referral type
  const assignReferralTypeMutation = useAssignReferralTypeMutation({
    onSuccess: () => {
      console.log('Referral type assigned successfully');
      setSuccess(true);
      setIsAssigning(false);
      setError(undefined);

      // Reload page after 2 seconds to reflect changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    },
    onError: (error: any) => {
      console.error('Error assigning referral type:', error);
      setError(error?.response?.data?.msg || 'Failed to join referral program');
      setIsAssigning(false);
      setSuccess(false);

      Sentry.captureException({
        message: 'Error assigning referral type',
        extra: { error, userId: id },
      });
    },
  });

  /**
   * Assign referral type to user
   * @param referralTypeId - The ID of the referral type to assign (default: 1)
   */
  const assignReferralType = async (referralTypeId: number = 1) => {
    if (!id) {
      setError('User ID not found');
      return;
    }

    try {
      setIsAssigning(true);
      setError(undefined);
      setSuccess(false);

      await assignReferralTypeMutation.mutateAsync({
        userId: id,
        referralTypeId,
      });
    } catch (err) {
      console.error('Error in assignReferralType:', err);
      setError('An unexpected error occurred');
      setIsAssigning(false);
    }
  };

  return {
    assignReferralType,
    isAssigning,
    error,
    success,
  };
};
